import { FC, Fragment } from 'react';
import { StyleSheet } from '@react-pdf/renderer';
import Typography from '../Pdf/components/Typography';
import Column from '../Pdf/components/Column';
import theme from '../Pdf/components/theme';
import getSectionNumbers from './helpers/get-section-numbers';
import PeriodEndReportPage from './components/pdf/PeriodEndReportPage';
import TableOfContentsEntry from './components/pdf/TableOfContentsEntry';
import Divider from './components/pdf/Divider';
import { PeriodEndReportPageProps, PeriodEndReportPdfLayout } from './types';

const styles = StyleSheet.create({
  title: {
    padding: theme.spacing(
      0,
      theme.spacingSizes.sm,
      theme.spacingSizes.md,
      theme.spacingSizes.sm,
    ),
  },
});

type Entry = {
  number: number;
  label: string;
  start: number;
  end: number;
  subEntries?: Array<Omit<Entry, 'subEntries'>>;
};

type Props = PeriodEndReportPageProps & {
  layout: PeriodEndReportPdfLayout;
  hasCommentary: boolean;
  hasFundValueBridge: boolean;
  hasFundValueProgression: boolean;
  hasFundPerformanceMetrics: boolean;
  hasFundCashFlowAndIrr: boolean;
  hasPortfolioUpdate: boolean;
};

const TableOfContents: FC<Props> = ({
  layout,
  hasCommentary,
  hasFundValueBridge,
  hasFundValueProgression,
  hasFundPerformanceMetrics,
  hasFundCashFlowAndIrr,
  hasPortfolioUpdate,
  ...rest
}) => {
  const {
    fundMetricsSectionNumber,
    portfolioUpdateSectionNumber,
    financialSectionNumber,
    disclaimerSectionNumber,
  } = getSectionNumbers(
    hasCommentary,
    hasFundValueBridge,
    hasFundValueProgression,
    hasFundPerformanceMetrics,
    hasFundCashFlowAndIrr,
    hasPortfolioUpdate,
  );

  const entries: Array<Entry> = [
    {
      number: 1,
      label: 'Fund overview',
      start: layout.fundOverview,
      end: layout.fundOverview,
    },
    {
      number: 2,
      label: 'Commitment overview',
      start: layout.commitmentOverview,
      end: layout.commitmentOverview,
    },
  ];

  if (layout.fundManagerCommentary) {
    entries.push({
      number: 3,
      label: 'Fund manager commentary',
      start: layout.fundManagerCommentary.start,
      end: layout.fundManagerCommentary.end,
    });
  }

  const fundMetricPages = [
    layout.fundValueBridge,
    layout.fundValueProgression,
    layout.fundPerformanceMetrics,
    layout.fundCashFlowAndIrr,
  ].filter((page) => page !== undefined);
  const hasFundMetrics = fundMetricPages.length > 0;

  if (hasFundMetrics) {
    const subEntries: Entry['subEntries'] = [];

    if (layout.fundValueBridge) {
      subEntries.push({
        number: 1,
        label: 'Fund value bridge',
        start: layout.fundValueBridge,
        end: layout.fundValueBridge,
      });
    }

    if (layout.fundValueProgression) {
      subEntries.push({
        number: [layout.fundValueBridge].filter(Boolean).length + 1,
        label: 'Fund value progression',
        start: layout.fundValueProgression,
        end: layout.fundValueProgression,
      });
    }

    if (layout.fundPerformanceMetrics) {
      subEntries.push({
        number:
          [layout.fundValueBridge, layout.fundValueProgression].filter(Boolean)
            .length + 1,
        label: 'Fund status breakdown',
        start: layout.fundPerformanceMetrics,
        end: layout.fundPerformanceMetrics,
      });
    }

    if (layout.fundCashFlowAndIrr) {
      subEntries.push({
        number:
          [
            layout.fundValueBridge,
            layout.fundValueProgression,
            layout.fundPerformanceMetrics,
          ].filter(Boolean).length + 1,
        label: 'Cash flows and IRR calculation',
        start: layout.fundCashFlowAndIrr,
        end: layout.fundCashFlowAndIrr,
      });
    }

    entries.push({
      number: fundMetricsSectionNumber,
      label: 'Fund performance summary',
      start: Math.min(...fundMetricPages),
      end: Math.max(...fundMetricPages),
      subEntries,
    });
  }

  if (layout.portfolioUpdate) {
    entries.push({
      number: portfolioUpdateSectionNumber,
      label: 'Portfolio updates',
      start: layout.portfolioUpdate.start,
      end: layout.portfolioUpdate.end,
    });
  }

  entries.push(
    {
      number: financialSectionNumber,
      label: 'Financial statements',
      start: layout.incomeStatement,
      end: layout.pcap,
      subEntries: [
        {
          number: 1,
          label: 'Income statement',
          start: layout.incomeStatement,
          end: layout.incomeStatement,
        },
        {
          number: 2,
          label: 'Balance sheet',
          start: layout.balanceSheet,
          end: layout.balanceSheet,
        },
        {
          number: 3,
          label: 'Cash flow statement',
          start: layout.cashFlowStatement,
          end: layout.cashFlowStatement,
        },
        {
          number: 4,
          label: 'Schedule of investments',
          start: layout.scheduleOfInvestments,
          end: layout.scheduleOfInvestments,
        },
        {
          number: 5,
          label: 'PCAP',
          start: layout.pcap,
          end: layout.pcap,
        },
      ],
    },
    {
      number: disclaimerSectionNumber,
      label: 'Disclaimer',
      start: layout.disclaimer,
      end: layout.disclaimer,
    },
  );

  return (
    <PeriodEndReportPage {...rest}>
      <Typography variant="h5" style={styles.title}>
        Table of contents
      </Typography>
      <Column spacing="xxs">
        {entries.map((entry, index) => {
          const isNotLast = index !== entries.length - 1;

          return (
            <Fragment key={entry.label}>
              <TableOfContentsEntry
                number={`0${entry.number}`}
                label={entry.label}
                pageStart={entry.start}
                pageEnd={entry.end}
              />
              {entry.subEntries
                ? entry.subEntries.map((subEntry) => (
                    <TableOfContentsEntry
                      key={subEntry.label}
                      number={`${entry.number}.${subEntry.number}`}
                      label={subEntry.label}
                      pageStart={subEntry.start}
                      pageEnd={subEntry.end}
                      subEntry
                    />
                  ))
                : null}
              {isNotLast ? <Divider /> : null}
            </Fragment>
          );
        })}
      </Column>
    </PeriodEndReportPage>
  );
};

export default TableOfContents;
